import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { ArrowPathIcon, ClipboardDocumentIcon, ExclamationTriangleIcon, HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/outline";
import { HandThumbDownIcon as HandThumbDownSolid, HandThumbUpIcon as HandThumbUpSolid } from "@heroicons/react/24/solid";
import { QuinnMessage, QuinnRole } from "../../types";
import type { QuinnRatingEnum } from "../../../../generated/enums.ts";
import { copyTextToClipboard, plainTextForQuinnCopy } from "../../utils";
import { QuinnMarkdown } from "../Markdown";
import { QuinnTrace } from "../Trace";
import { QuinnSources } from "../Sources";
import { QuinnPrompt } from "../Prompt";

type QuinnMessageViewProps = {
    message: QuinnMessage;
    isBusy: boolean;
    canRetry: boolean;
    onRetry: () => void;
    onAnswerPrompt: (values: string[], label: string) => void;
    onRate: (runId: string, rating: QuinnRatingEnum) => void;
};

export function QuinnMessageView({ message, isBusy, canRetry, onRetry, onAnswerPrompt, onRate }: QuinnMessageViewProps) {
    const prefersReducedMotion = useReducedMotion();
    const [hasCopied, setHasCopied] = useState(false);
    const copyableText = plainTextForQuinnCopy(message.content);
    const canCopy = copyableText.length > 0;

    async function copyMessage() {
        if (!canCopy) return;
        const copied = await copyTextToClipboard(copyableText);
        if (!copied) return;
        setHasCopied(true);
        setTimeout(() => setHasCopied(false), 1600);
    }

    if (message.role === QuinnRole.User) {
        return (
            <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.97, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="flex justify-end"
            >
                <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-sm font-medium text-white">
                    {message.content}
                </p>
            </motion.div>
        );
    }

    const showEmptyPlaceholder = message.isStreaming && message.content.length === 0;
    const canRate = Boolean(message.runId) && !message.isStreaming && !message.error;
    const showActions = !message.isStreaming && !message.error && (canCopy || canRetry || canRate);

    function ratingButton(rating: QuinnRatingEnum) {
        const isChosen = message.rating === rating;
        const Outline = rating === "up" ? HandThumbUpIcon : HandThumbDownIcon;
        const Solid = rating === "up" ? HandThumbUpSolid : HandThumbDownSolid;
        const Icon = isChosen ? Solid : Outline;
        return (
            <button
                type="button"
                aria-pressed={isChosen}
                aria-label={rating === "up" ? "Goed antwoord" : "Niet goed antwoord"}
                title={rating === "up" ? "Goed antwoord" : "Niet goed antwoord"}
                onClick={() => message.runId && onRate(message.runId, rating)}
                className={`inline-flex items-center rounded-md px-1.5 py-1 transition-colors hover:bg-gray-100 ${
                    isChosen ? "text-primary" : "text-gray-400 hover:text-gray-600"
                }`}
            >
                <Icon className="h-3.5 w-3.5" />
            </button>
        );
    }

    return (
        <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="space-y-2"
        >
            {message.trace.length > 0 && <QuinnTrace steps={message.trace} isStreaming={message.isStreaming} />}

            {message.error ? (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" />
                    <span>{message.error}</span>
                </div>
            ) : (
                <div className="min-w-0">
                    {showEmptyPlaceholder ? (
                        <div className="flex gap-1.5 py-1">
                            {[0, 1, 2].map((index) => (
                                <motion.span
                                    key={index}
                                    className="h-1.5 w-1.5 rounded-full bg-gray-300"
                                    animate={prefersReducedMotion ? undefined : { opacity: [0.3, 1, 0.3] }}
                                    transition={{ duration: 1.1, repeat: Infinity, delay: index * 0.16 }}
                                />
                            ))}
                        </div>
                    ) : (
                        <>
                            <QuinnMarkdown content={message.content} />
                            {message.isStreaming && (
                                <span className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 animate-pulse bg-primary align-middle" />
                            )}
                        </>
                    )}
                </div>
            )}

            {message.prompt && (
                <QuinnPrompt prompt={message.prompt} disabled={isBusy} onAnswer={onAnswerPrompt} />
            )}

            {!message.isStreaming && <QuinnSources sources={message.sources} />}

            {showActions && (
                <div className="flex items-center gap-1 pt-0.5">
                    {canCopy && (
                        <button
                            type="button"
                            onClick={copyMessage}
                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        >
                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                            {hasCopied ? "Gekopieerd" : "Kopiëren"}
                        </button>
                    )}
                    {canRate && (
                        <>
                            {ratingButton("up")}
                            {ratingButton("down")}
                        </>
                    )}
                    {canRetry && (
                        <button
                            type="button"
                            onClick={onRetry}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
                        >
                            <ArrowPathIcon className="h-3.5 w-3.5" />
                            Opnieuw
                        </button>
                    )}
                </div>
            )}
        </motion.div>
    );
}
